import type { RxJsonSchema } from 'rxdb'

/**
 * Comment Document Type (Denormalized for RxDB)
 */
export interface CommentDocument {
  id: string
  envId: string
  entityType: string // 'task', 'project', 'page', etc.
  entityId: string
  content: string
  contentFormat: string // 'tiptap', 'markdown', 'plain'
  contentPlainText: string | null
  parentCommentId: string | null
  anchorId: string | null // Links inline comments to text marks in the editor
  isDeleted: boolean
  isResolved: boolean
  resolvedAt: number | null
  resolvedBy: string | null

  // Denormalized user info
  createdByName: string | null
  createdByEmail: string | null

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Comment
 */
export const commentSchema: RxJsonSchema<CommentDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    entityType: {
      type: 'string',
      maxLength: 50,
    },
    entityId: {
      type: 'string',
      maxLength: 36,
    },
    content: {
      type: 'string',
      maxLength: 10000,
    },
    contentFormat: {
      type: 'string',
      maxLength: 20,
      default: 'tiptap',
    },
    contentPlainText: {
      type: ['string', 'null'],
      maxLength: 10000,
    },
    parentCommentId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    anchorId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },
    isResolved: {
      type: 'boolean',
      default: false,
    },
    resolvedAt: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    resolvedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // Denormalized fields
    createdByName: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    createdByEmail: {
      type: ['string', 'null'],
      maxLength: 255,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'entityType',
    'entityId',
    'content',
    'contentFormat',
    'isDeleted',
    'isResolved',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'entityId',
    'updatedOn',
    ['entityId', 'isDeleted'],
    ['envId', 'isDeleted'],
  ],
}

export const commentMigrationStrategies = {}
